/**
 * IntelSourceAgent — ask the research analyst about one intel source. The session is a conversation like any other:
 * `DebugSessionChat` over the session's thread (the shared conversation components), so a question can be followed up
 * and the answer reads as every answer does. The first question is framed with the source it is about.
 * Template example: demo `/chats` (an open chat) in a `Card`.
 */
import { useEffect } from 'react';
import { useMultiStepAgentStream } from '../../hooks/useMultiStepAgentStream.js';
import { DebugSessionChat } from '../conversations/DebugSessionChat.js';
import { Card } from '../ui/Card.js';

interface IntelSourceAgentProps {
  sourceId: number;
  sourceName: string;
  platform: string;
}

/** The tools the analyst answers about a source with. */
const SOURCE_TOOLS = ['query-intel-data', 'query-intel-sources', 'query-intel-network', 'summarize-source-activity'];

export function IntelSourceAgent({ sourceId, sourceName, platform }: IntelSourceAgentProps) {
  const stream = useMultiStepAgentStream();
  const key = `intel-source:${sourceId}`;
  // The hook re-renders for its active session only.
  const { selectStep } = stream;
  useEffect(() => {
    selectStep(key);
  }, [key, selectStep]);
  return (
    <Card title="Ask about this source">
      <div className="h-[560px] min-h-0">
        <DebugSessionChat
          session={stream.activeSession}
          actions={{
            execute: (params) => {
              stream.selectStep(key);
              return stream.execute(key, params);
            },
            sendFollowUp: (message) => stream.sendFollowUp(key, message),
            stop: () => stream.stop(),
            pause: () => stream.pause(),
            resume: () => stream.resume(),
            reset: () => stream.reset(key),
          }}
          executeParams={{ templateSlug: 'research-analyst', config: { tools: SOURCE_TOOLS } }}
          inputFor={(question) =>
            `Analyze intel source "${sourceName}" (platform: ${platform}, ID: ${sourceId}). User question: ${question}`
          }
        />
      </div>
    </Card>
  );
}
